import { useTranslations } from 'next-intl'
import { PropsWithChildren } from 'react'

import { RoomThumbnail, TRoomDetail } from '@/entities/room'
import { Link } from '@/shared/i18n'
import { cn } from '@/shared/lib'
import { Button, Dialog } from '@/shared/ui'

type Props = Pick<TRoomDetail, 'otherHostRooms'>

export const RoomDialogTrigger = ({
  children,
  otherHostRooms,
}: PropsWithChildren<Props>) => {
  return (
    <Dialog.Root>
      <Dialog.Trigger asChild>{children}</Dialog.Trigger>
      <RoomDialog otherHostRooms={otherHostRooms} />
    </Dialog.Root>
  )
}

export const RoomDialog = ({ otherHostRooms }: Props) => {
  const t = useTranslations('room_detail')

  return (
    <Dialog.Content className="h-[760px] gap-0 p-0">
      <Dialog.Header className="p-6">
        <Dialog.Title>{t('landlord_profile.other_unit')}</Dialog.Title>
      </Dialog.Header>
      <div className="scrollbar thick flex grow flex-col overflow-y-auto pr-6 pl-6">
        <RoomList otherHostRooms={otherHostRooms} size="large" />
      </div>
      <Dialog.Footer className="p-6">
        <Dialog.Close asChild>
          <Button size="large" color="secondary">
            {t('landlord_profile.button.close')}
          </Button>
        </Dialog.Close>
      </Dialog.Footer>
    </Dialog.Content>
  )
}

type RoomListProps = Pick<TRoomDetail, 'otherHostRooms'> & {
  size?: 'small' | 'large'
  disabled?: boolean
}

export const RoomList = ({
  otherHostRooms,
  size = 'small',
  disabled = false,
}: RoomListProps) => {
  const t = useTranslations('room_detail')

  return (
    <ul className="grid grid-cols-3 gap-x-5 gap-y-4">
      {otherHostRooms.map(room => (
        <li
          key={room.rid}
          className={cn({
            'w-21': size === 'small',
            'w-25': size === 'large',
          })}
        >
          <Link
            href={`/guest/room/${room.rid}`}
            target="_blank"
            className={cn(
              'cursor-pointer space-y-2',
              disabled && 'cursor-default',
            )}
            onClick={event => {
              if (disabled) {
                event.preventDefault()
              }
            }}
          >
            <RoomThumbnail
              src={room.picMain}
              alt={t('landlord_profile.unit_image', {
                roomName: room.roomName,
              })}
              ratio="1:1"
              size={size === 'small' ? 84 : 100}
              rounded
            />
            <h4 className="typo-label2 line-clamp-1 font-normal text-gray-900">
              {room.roomName}
            </h4>
          </Link>
        </li>
      ))}
    </ul>
  )
}
